import { useEffect, useRef } from "react";

export default function TechSaraf() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // Pre-generate background Hexagons
    interface Hexagon {
      x: number;
      y: number;
      radius: number;
      rotation: number;
      rotationSpeed: number;
      maxOpacity: number;
      pulsePhase: number;
      pulseSpeed: number;
    }
    const hexagons: Hexagon[] = [];
    const hexCount = 12;

    const spawnHexagons = () => {
      hexagons.length = 0;
      for (let i = 0; i < hexCount; i++) {
        hexagons.push({
          x: Math.random() * width,
          y: Math.random() * height,
          radius: 40 + Math.random() * 80,
          rotation: Math.random() * Math.PI,
          rotationSpeed: (Math.random() - 0.5) * 0.002,
          maxOpacity: 0.1 + Math.random() * 0.2,
          pulsePhase: Math.random() * Math.PI * 2,
          pulseSpeed: 0.005 + Math.random() * 0.01,
        });
      }
    };

    const drawHexagon = (c: CanvasRenderingContext2D, h: Hexagon) => {
      const opacity = h.maxOpacity * (0.6 + Math.sin(h.pulsePhase) * 0.4);
      c.save();
      c.translate(h.x, h.y);
      c.rotate(h.rotation);
      c.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        const px = h.radius * Math.cos(angle);
        const py = h.radius * Math.sin(angle);
        if (i === 0) c.moveTo(px, py);
        else c.lineTo(px, py);
      }
      c.closePath();
      c.strokeStyle = `rgba(14, 165, 233, ${opacity})`;
      c.lineWidth = 1.5;
      
      // Light outer glow
      c.shadowBlur = 10;
      c.shadowColor = "rgba(14, 165, 233, 0.3)";
      c.stroke();
      c.shadowBlur = 0;

      // Hexagon inner styling (draw a smaller inner concentric hexagon)
      if (h.radius > 60) {
        c.beginPath();
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i;
          const px = (h.radius * 0.7) * Math.cos(angle);
          const py = (h.radius * 0.7) * Math.sin(angle);
          if (i === 0) c.moveTo(px, py);
          else c.lineTo(px, py);
        }
        c.closePath();
        c.strokeStyle = `rgba(167, 139, 250, ${opacity * 0.5})`;
        c.stroke();
      }

      // Hexagon soft fill
      c.fillStyle = `rgba(14, 165, 233, ${opacity * 0.04})`;
      c.fill();
      c.restore();
    };

    // Printed Circuit Board (PCB) Circuit Line
    class CircuitLine {
      points: { x: number; y: number }[] = [];
      pulseProgress = 0;
      pulseSpeed = 0;
      color = "";
      glowColor = "";
      lineWidth = 1;
      hasPulse = false;

      constructor() {
        this.reset();
      }

      reset() {
        this.points = [];
        const startX = -100 - Math.random() * 200;
        const startY = Math.random() * height;
        
        // Randomly pick cyber cyan, neon purple, or electric blue
        const rand = Math.random();
        if (rand < 0.45) {
          this.color = "rgba(14, 165, 233, 0.45)"; // cyan
          this.glowColor = "rgba(14, 165, 233, 0.6)";
        } else if (rand < 0.8) {
          this.color = "rgba(167, 139, 250, 0.45)"; // purple
          this.glowColor = "rgba(167, 139, 250, 0.6)";
        } else {
          this.color = "rgba(56, 189, 248, 0.5)"; // light blue
          this.glowColor = "rgba(56, 189, 248, 0.7)";
        }

        this.lineWidth = Math.random() * 1.5 + 1.2;
        this.pulseSpeed = 0.002 + Math.random() * 0.0035;
        this.pulseProgress = -Math.random() * 0.5; // Staggered delay starting
        this.hasPulse = Math.random() > 0.1;

        let cx = startX;
        let cy = startY;
        this.points.push({ x: cx, y: cy });

        const segmentCount = 3 + Math.floor(Math.random() * 4);
        for (let s = 0; s < segmentCount; s++) {
          const remainingWidth = width - cx;
          if (remainingWidth <= 60) break;

          const type = Math.random();
          if (type < 0.5) {
            // Straight Horizontal segment
            const len = 100 + Math.random() * 200;
            cx += len;
            this.points.push({ x: cx, y: cy });
          } else {
            // 45-degree angle bend
            const dy = (Math.random() > 0.5 ? 1 : -1) * (30 + Math.random() * 70);
            const dx = Math.abs(dy); // dx = |dy| strictly yields a 45-degree angle
            cx += dx;
            cy += dy;
            this.points.push({ x: cx, y: cy });
          }
        }
      }

      update() {
        if (this.hasPulse) {
          this.pulseProgress += this.pulseSpeed;
          if (this.pulseProgress > 1) {
            this.pulseProgress = -Math.random() * 0.3; // Staggered delay loop
            if (Math.random() < 0.15) {
              this.reset();
            }
          }
        }
      }

      draw(c: CanvasRenderingContext2D) {
        if (this.points.length < 2) return;

        // 1. Draw the track lines
        const firstPoint = this.points[0];
        if (firstPoint) {
          c.beginPath();
          c.moveTo(firstPoint.x, firstPoint.y);
          for (let i = 1; i < this.points.length; i++) {
            const pt = this.points[i];
            if (pt) {
              c.lineTo(pt.x, pt.y);
            }
          }
          c.strokeStyle = this.color;
          c.lineWidth = this.lineWidth;
          c.stroke();
        }

        // 2. Draw terminal pads (filled circles) at the ends
        const lastPoint = this.points[this.points.length - 1];
        if (lastPoint) {
          c.beginPath();
          c.arc(lastPoint.x, lastPoint.y, 4, 0, Math.PI * 2);
          c.fillStyle = this.color;
          c.fill();
          c.beginPath();
          c.arc(lastPoint.x, lastPoint.y, 1.5, 0, Math.PI * 2);
          c.fillStyle = "#ffffff";
          c.fill();
        }

        // 3. Draw joint dots at bends
        for (let i = 1; i < this.points.length - 1; i++) {
          const pt = this.points[i];
          if (pt) {
            c.beginPath();
            c.arc(pt.x, pt.y, 2, 0, Math.PI * 2);
            c.fillStyle = this.color;
            c.fill();
          }
        }

        // 4. Draw active electron pulse
        if (this.hasPulse && this.pulseProgress > 0) {
          const pos = this.getPointAt(this.pulseProgress);
          if (pos) {
            c.beginPath();
            c.arc(pos.x, pos.y, 4.5, 0, Math.PI * 2);
            c.fillStyle = "rgba(255, 255, 255, 0.95)";
            
            c.shadowBlur = 12;
            c.shadowColor = this.glowColor;
            c.fill();
            c.shadowBlur = 0; // reset

            c.beginPath();
            c.arc(pos.x, pos.y, 1.8, 0, Math.PI * 2);
            c.fillStyle = "#ffffff";
            c.fill();
          }
        }
      }

      getPointAt(progress: number) {
        if (this.points.length < 2) return null;
        let totalLen = 0;
        const lens: number[] = [];
        for (let i = 0; i < this.points.length - 1; i++) {
          const p1 = this.points[i];
          const p2 = this.points[i + 1];
          if (p1 && p2) {
            const d = Math.hypot(p2.x - p1.x, p2.y - p1.y);
            lens.push(d);
            totalLen += d;
          }
        }

        const target = progress * totalLen;
        let acc = 0;
        for (let i = 0; i < lens.length; i++) {
          const lenVal = lens[i];
          if (lenVal !== undefined) {
            if (acc + lenVal >= target) {
              const ratio = (target - acc) / lenVal;
              const p1 = this.points[i];
              const p2 = this.points[i + 1];
              if (p1 && p2) {
                return {
                  x: p1.x + (p2.x - p1.x) * ratio,
                  y: p1.y + (p2.y - p1.y) * ratio,
                };
              }
            }
            acc += lenVal;
          }
        }
        return this.points[this.points.length - 1] || null;
      }
    }

    const tracks: CircuitLine[] = [];
    const trackCount = 8; // Reduced from 18 for performance

    for (let i = 0; i < trackCount; i++) {
      tracks.push(new CircuitLine());
    }

    spawnHexagons();

    let lastTime = 0;
    const fpsInterval = 1000 / 30; // 30 FPS throttle

    const draw = (time: number) => {
      animationFrameId = requestAnimationFrame(draw);
      
      const elapsed = time - lastTime;
      if (elapsed < fpsInterval) return;
      lastTime = time - (elapsed % fpsInterval);

      ctx.clearRect(0, 0, width, height);

      // 1. Draw Hexagons first (background layer)
      hexagons.forEach((hex) => {
        hex.pulsePhase += hex.pulseSpeed;
        hex.rotation += hex.rotationSpeed;
        drawHexagon(ctx, hex);
      });

      // 2. Draw Circuit lines & pulses (foreground layer)
      tracks.forEach((track) => {
        track.update();
        track.draw(ctx);
      });
    };

    animationFrameId = requestAnimationFrame(draw);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      spawnHexagons();
      tracks.forEach((t) => t.reset());
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-10"
      style={{ mixBlendMode: "screen" }}
    />
  );
}
